import { gql } from '@apollo/client';

/**
 * The fundamental style factor model — exposures, factor returns and
 * attribution, from the institutional branch.
 *
 * A cross-sectional model (`IN-FUND-1.0`): every day it regresses stock
 * returns on industry dummies and thirteen style factors, so a security's
 * return decomposes into market + industry + styles + what is left over, which
 * is the specific return. That decomposition is what the attribution reads.
 *
 * ONE SET OF QUERIES SERVES EVERY ENTITY. Pass a symbol and the server works
 * out what it is — a stock (`HDFCBANK.NSE`), a mutual fund (`101762.IN`, any
 * plan's symbol resolving to regular-growth), an asset-model variant
 * (`balanced_growth_plus`) or a portfolio/goal (`3_buyhome_goal`). The answer
 * says which in `entityType`.
 *
 * UNITS, and they are not what a reader assumes. Returns and risk come back
 * ALREADY IN PERCENT — 5.81 is 5.81%, 14.48 is 14.48% annualised. Multiplying
 * by a hundred, as one does for a fraction, reports a 5.8% quarter as 581%.
 * Exposures are different again: cross-sectional z-scores, 0 being the market
 * average and 1 a standard deviation.
 *
 * COVERAGE cuts two ways. On a fund, model or portfolio it is the share of the
 * book resolved to priced equity, and exposures and attribution are DILUTED by
 * it — a 70% equity fund shows 70% of an all-equity book's tilt. Risk does the
 * opposite: it renormalises the equity sleeve to 100% and reports how big that
 * sleeve was in `equityCoverage`. The two numbers are not comparable without
 * saying so.
 */

/**
 * The snapshot's fields — what the "Fundamental style factors" card reads —
 * written once. Selected here off the top-level query, and off a fund or
 * portfolio record through FACTOR_VIEW_FIELDS (queries/factors.js), so the two
 * pages cannot drift apart. A plain string, not a fragment: see
 * CLASSIFICATION_SELECTION in portfolios.js.
 */
export const STYLE_SNAPSHOT_FIELDS = `
  asOfDate
  modelVersion
  entityType
  industry
  coverage
  exposures { factor exposure volAdjustedExposure }
  latestAttribution { asOfDate componentType componentName contribution }
  risk {
    riskKind
    benchmarkSymbol
    totalRisk
    factorRisk
    specificRisk
    equityCoverage
    familyContributions { componentName contribution }
    factors { factorName exposure contribution }
  }
`;

/**
 * One holding's piece of each exposure — weight × the stock's own z-score,
 * summing across holdings to the entity's exposure. Both sources answer with
 * this row type (FundamentalStyleFactorHoldingContributionType).
 */
export const STYLE_HOLDING_CONTRIBUTION_ROW = `
  holdingSecuritySymbol
  asOfDate
  entityType
  weight
  contributions { factor exposure volAdjustedExposure }
`;

/**
 * Everything about one entity on one day, in a single call: its exposures,
 * that day's return attribution, and its risk decomposition.
 *
 * The three used to be three round trips. `asOfDate` omitted is the latest
 * stored day. `risk` is empty for a stock — a single security has no
 * portfolio to decompose.
 */
export const STYLE_FACTOR_SNAPSHOT = gql`
  query StyleFactorSnapshot($securitySymbol: String!, $asOfDate: String) {
    fundamentalStyleFactors(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      ${STYLE_SNAPSHOT_FIELDS}
    }
  }
`;

/**
 * The full risk decomposition.
 *
 * Two rows unless `riskKind` narrows it: `total` is standalone volatility,
 * `active` is tracking error against the broad-market index in
 * `benchmarkSymbol`. The identities the UI can rely on:
 *
 *   Σ factors.contribution + specificContribution = totalRisk
 *   totalRisk² = factorRisk² + specificRisk²
 *   factorVarianceShare + specificVarianceShare = 1
 *
 * `familyContributions` is the same sum grouped into market / style /
 * industry. A FUND's rows are compact — per-factor exposure and volatility
 * come back as 0 and `topSpecificContributors` empty — so a fund shows its
 * contributions and family split, and the holdings come from the holding
 * attribution instead.
 */
export const STYLE_FACTOR_RISK = gql`
  query StyleFactorRisk($securitySymbol: String!, $asOfDate: String, $riskKind: String) {
    fundamentalStyleFactorRisk(
      securitySymbol: $securitySymbol
      asOfDate: $asOfDate
      riskKind: $riskKind
    ) {
      asOfDate
      riskKind
      benchmarkSymbol
      totalRisk
      factorRisk
      specificRisk
      specificContribution
      factorVarianceShare
      specificVarianceShare
      equityCoverage
      nStocks
      grossWeight
      halfLife
      familyContributions { componentName contribution }
      factors { factorName factorType exposure volatility contribution }
      topSpecificContributors { securitySymbol weight specificVolatility contribution }
    }
  }
`;

/**
 * THE SAME RISK, MEASURED RATHER THAN FORECAST.
 *
 * `fundamentalStyleFactorRisk` carries a second set of columns beside the
 * forecast: what the book's weight path ACTUALLY did over the trailing window,
 * split the same way. Every cell is 252·Cov(w·c, r)/sigma, and covariances with
 * the total are additive, so the same numbers read both ways —
 *
 *   sum over holdings of totalContribution  = realizedTotalRisk
 *   sum over realizedFactors                = the factor part only
 *   sum over holdings of specificContribution = realizedTotalRisk - that
 *
 * which is why one query answers both "which factor carried the risk" and
 * "which holding carried it", and why a factor opened up lists the holdings
 * that made it.
 *
 * ASKED ON ITS OWN, not folded into STYLE_FACTOR_RISK, for two reasons. It is
 * large — a fund's ACTIVE row carries a cell per benchmark constituent, 622
 * holdings on the fund sampled, ~945KB of JSON — and it is only ever read on
 * the Realised sub-tab of Risk, so the forecast views should not pay for it.
 *
 * `exposure` and `volatility` are NOT requested on `realizedFactors`: a
 * realized contribution is a covariance, not a loading times a vol, and the
 * resolver returns 0 for both. Asking for them would put two columns of zeros
 * on screen that read as missing data rather than as inapplicable.
 */
export const STYLE_REALIZED_RISK = gql`
  query StyleRealizedRisk($securitySymbol: String!, $asOfDate: String, $riskKind: String) {
    fundamentalStyleFactorRisk(
      securitySymbol: $securitySymbol
      asOfDate: $asOfDate
      riskKind: $riskKind
    ) {
      asOfDate
      riskKind
      benchmarkSymbol
      entityType
      realizedTotalRisk
      realizedFactorRisk
      realizedSpecificRisk
      realizedObservations
      realizedFamilyContributions { componentName contribution }
      realizedFactors { factorName factorType contribution }
      realizedHoldingContributions {
        holdingSecuritySymbol
        industry
        weight
        totalContribution
        factorContribution
        specificContribution
        specificVolatility
        byFactor { componentType componentName contribution }
      }
    }
  }
`;

/**
 * Whether the model has been fitted recently, and which days are missing.
 * `latestFittableDate` is the newest day the inputs exist for — the furthest
 * a run can be asked to reach.
 */
export const STYLE_FACTOR_STATUS = gql`
  query StyleFactorStatus($lookbackDays: Int) {
    fundamentalStyleFactorStatus(lookbackDays: $lookbackDays) {
      modelVersion
      latestFittedDate
      latestFittableDate
      isCurrent
      missingDates
    }
  }
`;

/** The model itself: its factors, what each means, and the industry buckets. */
export const STYLE_FACTOR_MODEL = gql`
  query StyleFactorModel {
    fundamentalStyleFactorModel {
      version
      winsorMadLimit
      capWeightedMeanZero
      minUniverseSize
      maxWeightShare
      industryBuckets
      styleFactors {
        name
        label
        column
        rationale
        descriptors { name source transform direction weight }
      }
    }
  }
`;

/**
 * One security's style exposures through time.
 *
 * `exposure` is a z-score against the estimation universe — 0 is the market,
 * ±1 is a standard deviation — so it is comparable across factors in a way a
 * raw fundamental is not. `coverage` says how much of the entity could be
 * scored, which is what makes a fund's exposures trustworthy or not.
 */
export const STYLE_FACTOR_EXPOSURES = gql`
  query StyleFactorExposures($securitySymbol: String!, $startDate: String, $endDate: String) {
    fundamentalStyleFactorExposures(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      securitySymbol
      asOfDate
      modelVersion
      entityType
      industry
      subIndustry
      marketCap
      inEstimationUniverse
      coverage
      exposures { factor exposure volAdjustedExposure }
    }
  }
`;

/**
 * What each factor earned, per day, across the whole market.
 *
 * `factorType` separates the market, the industry dummies and the styles.
 * `tStatistic` is whether that day's estimate means anything — a factor return
 * with |t| under about 2 is noise, and plotting it as signal is how a model
 * gets over-read.
 */
export const STYLE_FACTOR_RETURNS = gql`
  query StyleFactorReturns($factorNames: [String!], $startDate: String, $endDate: String) {
    fundamentalStyleFactorReturns(
      factorNames: $factorNames
      startDate: $startDate
      endDate: $endDate
    ) {
      asOfDate
      modelVersion
      factorName
      factorType
      factorReturn
      stdError
      tStatistic
      nObservations
      rSquared
      adjRSquared
    }
  }
`;

/**
 * One security's return split into what the model explains and what it does
 * not. `specificReturn` is the residual — the part that is this security
 * rather than its styles — which is the number a stock picker is paid for.
 */
export const STYLE_SPECIFIC_RETURNS = gql`
  query StyleSpecificReturns($securitySymbol: String!, $startDate: String, $endDate: String) {
    fundamentalStyleFactorSpecificReturns(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      securitySymbol
      asOfDate
      totalReturn
      fittedReturn
      specificReturn
      regressionWeight
    }
  }
`;

/**
 * Return attributed to each factor over a window: exposure times factor
 * return, summed. `period` is the window's total per component, `daily` the
 * path that got there.
 */
export const STYLE_FACTOR_ATTRIBUTION = gql`
  query StyleFactorAttribution($securitySymbol: String!, $startDate: String, $endDate: String) {
    equityStyleFactorAttribution(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      securitySymbol
      entityType
      modelVersion
      startDate
      endDate
      days
      entityReturn
      coverage
      period { asOfDate componentType componentName contribution }
      daily { asOfDate componentType componentName contribution }
    }
  }
`;

/** The same attribution, but which HOLDING contributed each piece. */
export const STYLE_HOLDING_ATTRIBUTION = gql`
  query StyleHoldingAttribution($securitySymbol: String!, $startDate: String!, $endDate: String!) {
    equityStyleFactorHoldingAttribution(
      securitySymbol: $securitySymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      securitySymbol
      modelVersion
      startDate
      endDate
      days
      modelledReturn
      entityReturn
      averageCoverage
      totals { componentType componentName contribution }
      holdings {
        holdingSecuritySymbol
        industry
        averageWeight
        market
        industryContribution
        specific
        total
        # Which STYLE drove this holding — the grouping that turns a list of
        # contributors into an explanation.
        styles { componentName contribution }
      }
    }
  }
`;

/** Style exposures rolled up by industry — where the market's tilts sit. */
export const STYLE_EXPOSURES_BY_INDUSTRY = gql`
  query StyleExposuresByIndustry($asOfDate: String, $weighting: String!) {
    fundamentalStyleFactorExposuresByIndustry(asOfDate: $asOfDate, weighting: $weighting) {
      asOfDate
      group
      stocks
      weightShare
      exposures { factor exposure volAdjustedExposure }
    }
  }
`;

/**
 * Risk through time, for one entity.
 *
 * `riskKind` is REQUIRED here, unlike the point-in-time read where omitting it
 * returns both — so a caller must say whether it wants standalone volatility
 * ("total") or tracking error ("active").
 */
export const STYLE_RISK_HISTORY = gql`
  query StyleRiskHistory(
    $securitySymbol: String!
    $riskKind: String!
    $startDate: String
    $endDate: String
  ) {
    fundamentalStyleFactorRiskHistory(
      securitySymbol: $securitySymbol
      riskKind: $riskKind
      startDate: $startDate
      endDate: $endDate
    ) {
      asOfDate
      totalRisk
      factorRisk
      specificRisk
      factorVarianceShare
      familyContributions { componentName contribution }
    }
  }
`;

/* ── roll-ups across the market ────────────────────────────────────────────
   Attribution and exposures grouped by industry, or by any security group
   (Major Sectors, Asset Allocation, …). `weighting` is "cap" or "equal".

   NOTE THE SHAPE: these take a single `asOfDate`, not a range. There is no
   period version — a run of days means a call per day, which is why the UI
   offers a date rather than a window. */

export const STYLE_ATTRIBUTION_BY_INDUSTRY = gql`
  query StyleAttributionByIndustry($asOfDate: String, $weighting: String!) {
    equityStyleFactorAttributionByIndustry(asOfDate: $asOfDate, weighting: $weighting) {
      asOfDate
      group
      stocks
      weightShare
      groupReturn
      components { componentType componentName contribution }
    }
  }
`;

export const STYLE_ATTRIBUTION_BY_GROUP = gql`
  query StyleAttributionByGroup($securityGroupId: Int!, $asOfDate: String, $weighting: String!) {
    equityStyleFactorAttributionBySecurityGroup(
      securityGroupId: $securityGroupId
      asOfDate: $asOfDate
      weighting: $weighting
    ) {
      asOfDate
      group
      stocks
      weightShare
      groupReturn
      components { componentType componentName contribution }
    }
  }
`;

export const STYLE_EXPOSURES_BY_GROUP = gql`
  query StyleExposuresByGroup($securityGroupId: Int!, $asOfDate: String, $weighting: String!) {
    fundamentalStyleFactorExposuresBySecurityGroup(
      securityGroupId: $securityGroupId
      asOfDate: $asOfDate
      weighting: $weighting
    ) {
      asOfDate
      group
      stocks
      weightShare
      exposures { factor exposure volAdjustedExposure }
    }
  }
`;

/**
 * Each HOLDING's contribution to the entity's style exposure.
 *
 * The other half of the per-stock picture. `equityStyleFactorHoldingAttribution`
 * says which stock produced the RETURN a style earned; this says which stock
 * produced the EXPOSURE that earned it — the two columns per factor the
 * regression grid has carried all along.
 *
 * Each `contributions` row is weight × that stock's own exposure, so the rows
 * SUM to the entity's exposure: summed over the 56 holdings of the flexi cap
 * sampled, every one of the 13 factors matched `fundamentalStyleFactors` to
 * four decimal places. Which is why the grid totals them rather than averaging.
 *
 * POINT IN TIME, unlike the attribution beside it: this is one `asOfDate` and
 * carries that day's weight, while the return contributions are summed over a
 * window and carry the average weight across it. The two weights differ (0.723
 * against 0.633 on the same fund) and the screen says which is which.
 */
export const STYLE_HOLDING_CONTRIBUTIONS = gql`
  query StyleHoldingContributions($securitySymbol: String!) {
    fundamentalStyleFactorHoldingContributions(securitySymbol: $securitySymbol) {
      ${STYLE_HOLDING_CONTRIBUTION_ROW}
    }
  }
`;

/**
 * The same per-holding rows, COMPUTED from the entity's current book rather
 * than read from the stored table. A portfolio booked today has no stored
 * contributions until the nightly run writes them, but it has holdings — so
 * the portfolio page opens its exposure rows onto this.
 */
export const STYLE_ANALYSIS_HOLDINGS = gql`
  query StyleAnalysisHoldings($securitySymbol: String!) {
    fundamentalStyleFactorAnalysis(securitySymbol: $securitySymbol) {
      asOfDate
      holdingContributions {
        ${STYLE_HOLDING_CONTRIBUTION_ROW}
      }
    }
  }
`;

/**
 * WHAT IS DRIVING THE RISK — one entity's σ read by STOCK and by FACTOR.
 *
 * Computed on the fly from the entity's book and the day's stored stock inputs,
 * so it needs no stored risk row of its own — which is why it can answer for a
 * fund the nightly risk stage has not written.
 *
 * `riskKind` is the whole point of the panel:
 *   "total"  — the equity sleeve's own volatility.
 *   "active" — tracking error against the benchmark. The weights are ACTIVE
 *              weights, so a name the benchmark holds and the fund does not
 *              appears with a NEGATIVE weight and still contributes risk. Not
 *              holding something is a bet.
 *
 * The identities the resolver states, and which the screen checks rather than
 * trusts: Σ holdings.totalContribution = totalRisk = Σ factors.contribution +
 * specificTotal.
 */
export const STYLE_RISK_BY_HOLDING = gql`
  query StyleRiskByHolding($securitySymbol: String!, $asOfDate: String, $riskKind: String!) {
    fundamentalStyleFactorRiskByHolding(
      securitySymbol: $securitySymbol
      asOfDate: $asOfDate
      riskKind: $riskKind
    ) {
      securitySymbol
      entityType
      asOfDate
      modelVersion
      riskKind
      benchmarkSymbol
      totalRisk
      factorRisk
      specificRisk
      specificTotal
      # Both lists are attribution rows — componentType / componentName — not
      # the exposure-bearing risk-factor row the stored risk read returns.
      factors { componentType componentName contribution }
      holdings {
        holdingSecuritySymbol
        industry
        weight
        totalContribution
        factorContribution
        specificContribution
        specificVolatility
        byFactor { componentType componentName contribution }
      }
    }
  }
`;

/**
 * THE LOOK-THROUGH AS PATHS, for the three exposure families.
 *
 * One row per complete route from the entity to a leaf stock — portfolio →
 * model → fund → stock — with the route's share of the entity (`pathWeight`)
 * and, per family, that share times the stock's own figure. Rows sum to the
 * entity's figure however they are grouped: by the fund at level 1, by the
 * stock across every route, or both as a tree. So one response serves the
 * drill under every sector bucket, style factor and regression loading, and
 * the client only ever groups and sums.
 *
 * Three families are selected, because the Exposures panel shows three:
 *   exposures (buckets)     the classification buckets — sector, industry,
 *                           sub-industry, country, asset class, security
 *                           type — grouped by the server, each item naming
 *                           the rows inside it (\`pathIds\`), so a bucket's
 *                           drill is those rows and nothing is re-classified
 *                           here
 *   exposureContributions   pathWeight × the stock's style z-score
 *   regressionExposures     pathWeight × the stock's regression beta / scores
 *
 * `regressionOwn` is the level-1 holdings' OWN regression figures, weighted —
 * a fund's beta is measured on its NAV, its stocks' on their prices, so the
 * rows under a fund need not sum to the fund's own; the gap is real (cash,
 * fees, weight drift, disclosure lag) and is shown beside the sum rather than
 * hidden. Style and classification carry no such gap: they tie exactly.
 *
 * Verified against the backend on 2026-10-02 on a fund (49 rows), a model
 * (438) and a portfolio (1,022): Σ pathWeight = coverage, Σ style
 * contributions = the entity's exposure for all 13 styles, and a bucket
 * item's weight = Σ pathWeight of its pathIds.
 *
 * `path[].weight` is the node's share of ITS PARENT; `pathWeight` is the
 * product down the route. `resolved` false: the stock had no exposure row that
 * day — its weight is kept and its style contributions are empty.
 *
 * Forecast and realised risk, returns and fundamentals ride on the same rows
 * and are computed only when selected; they get their own documents as those
 * drills are built.
 */
export const EXPOSURES_LOOK_THROUGH = gql`
  query ExposuresLookThrough($securitySymbol: String!, $asOfDate: String) {
    lookThroughContributions(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      securitySymbol
      entityType
      asOfDate
      disclosureDate
      coverage
      unresolvedWeight
      # The entity's own style tilts — what the rows' contributions sum to.
      styleExposures { factor exposure }
      # The classification buckets, grouped server-side (all six when no
      # \`buckets\` is named): an item's pathIds are the rows inside it.
      exposures {
        bucket
        unclassifiedWeight
        items {
          name
          weight
          pathIds
          byHolding { securitySymbol name kind weight }
          byStock { stockSymbol name weight }
        }
      }
      regressionOwn { securitySymbol kind weight values { key value } }
      rows {
        pathId
        pathWeight
        resolved
        stockSymbol
        stockName
        path { securitySymbol name kind weight level }
        exposureContributions { factor exposure }
        regressionExposures { key value }
      }
    }
  }
`;

/**
 * THE LOOK-THROUGH AS PATHS, for the risk families.
 *
 * The same rows as EXPOSURES_LOOK_THROUGH, carrying the two risk measurements
 * the model can split along a route:
 *
 *   riskTotal / riskActive   the forecast (style model) σ — standalone and
 *                            against the benchmark — Euler-split per route:
 *                            total, factor and specific parts, and the factor
 *                            part by factor. Σ rows + Σ benchmarkOnly =
 *                            totalRisk; for the standalone kind benchmarkOnly
 *                            is empty and the rows alone tie.
 *   realizedRiskContribution the realised σ of the stock book over the window,
 *                            Euler-split per route. Σ rows = realizedRisk.totalRisk.
 *
 * Both kinds in one document, by alias, so one response serves both halves of
 * the risk view. `benchmarkOnly` is the names the index holds and the entity
 * does not: they have no route, carry a real (often negative) contribution to
 * the tracking error, and are what the rows leave of the active total — the
 * drill prints that gap as its own line rather than hiding it.
 *
 * Verified on the server 2026-10-02: on a model and a fund the standalone
 * rows tie to totalRisk and to every factor (1e-14); the active rows plus
 * benchmarkOnly tie to the tracking error; realised rows tie to the window's
 * σ. Simulation risk and scenarios are not on these rows yet.
 */
export const RISK_LOOK_THROUGH = gql`
  query RiskLookThrough($securitySymbol: String!, $asOfDate: String, $windowDays: Int! = 365) {
    lookThroughContributions(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      securitySymbol
      entityType
      asOfDate
      disclosureDate
      coverage
      riskTotal: risk(riskKind: "total") {
        riskKind benchmarkSymbol totalRisk factorRisk specificRisk specificTotal equityCoverage
        factors { componentType componentName contribution }
        benchmarkOnly { stockSymbol totalContribution factorContribution specificContribution byFactor { componentName contribution } }
      }
      riskActive: risk(riskKind: "active") {
        riskKind benchmarkSymbol totalRisk factorRisk specificRisk specificTotal equityCoverage
        factors { componentType componentName contribution }
        benchmarkOnly { stockSymbol totalContribution factorContribution specificContribution byFactor { componentName contribution } }
      }
      realizedRisk(windowDays: $windowDays) { windowDays observations totalRisk equityCoverage ownTotalRisk }
      rows {
        pathId
        pathWeight
        resolved
        stockSymbol
        stockName
        path { securitySymbol name kind weight level }
        riskTotal: risk(riskKind: "total") { totalContribution factorContribution specificContribution byFactor { componentName contribution } }
        riskActive: risk(riskKind: "active") { totalContribution factorContribution specificContribution byFactor { componentName contribution } }
        realizedRiskContribution(windowDays: $windowDays)
      }
    }
  }
`;

/**
 * THE LOOK-THROUGH AS PATHS, for valuation.
 *
 * Each route carries the leaf stock's OWN fundamentals — P/E, P/B, dividend
 * yield, ROE, debt to equity — unweighted, with `fundamentalSpecs` saying
 * which roll up harmonically (the price multiples). A node's figure is the
 * weighted mean of the stocks under it, weights = pathWeight, harmonic where
 * the spec says so; it is NOT a sum, which is why the valuation drill has an
 * aggregate mode of its own. `fundamentalsOwn` is each level-1 fund's own
 * stored figure, shown beside the mean of its stocks: the two come from
 * different sources (stock rows against fund tags) and agree only roughly.
 *
 * This is what lets a MODEL be valued at all: a model holds funds, which have
 * no earnings of their own, and the page refused to value one. Along the
 * routes it is the stocks being valued, weighted by how much of the model
 * reaches each.
 */
export const VALUATION_LOOK_THROUGH = gql`
  query ValuationLookThrough($securitySymbol: String!, $asOfDate: String) {
    lookThroughContributions(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      securitySymbol
      entityType
      asOfDate
      disclosureDate
      coverage
      fundamentalSpecs { metric harmonic }
      fundamentalsOwn { securitySymbol kind weight values { key value } }
      rows {
        pathId
        pathWeight
        resolved
        stockSymbol
        stockName
        path { securitySymbol name kind weight level }
        fundamentals { metric value harmonic }
      }
    }
  }
`;

/**
 * THE LOOK-THROUGH AS PATHS, for returns.
 *
 * Each route carries `pathWeight × the leaf stock's price return` per horizon,
 * in percentage points of the entity — its contribution to the return, at
 * TODAY's weights, the way the stored holding return contributions are built.
 * The rows add, so any grouping of them (by fund, by stock) sums to the same
 * figure.
 *
 * `returnsOwn` is each level-1 holding's OWN return × its weight — a fund's NAV
 * return, not its stocks'. The two differ for real reasons (cash, debt legs,
 * fees, weight drift, disclosure lag), so the drill shows a fund's own beside
 * the sum of its routes and prints the gap rather than hiding it.
 *
 * Horizons are the resolver's: 1d, 1w, 1m, 3m, 6m, ytd, 1y.
 */
export const RETURNS_LOOK_THROUGH = gql`
  query ReturnsLookThrough($securitySymbol: String!, $asOfDate: String, $horizons: [String!]) {
    lookThroughContributions(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      securitySymbol
      entityType
      asOfDate
      disclosureDate
      coverage
      returnsOwn(horizons: $horizons) { securitySymbol kind weight values { key value } }
      rows {
        pathId
        pathWeight
        resolved
        stockSymbol
        stockName
        path { securitySymbol name kind weight level }
        returns(horizons: $horizons) { key value }
      }
    }
  }
`;

/**
 * THE LOOK-THROUGH AS PATHS, for simulation (Monte-Carlo) risk — one scenario.
 *
 * Each route carries its leaf stock's contribution to the scenario's forecast
 * figures — volatility, VaR, CVaR, expected return — times the route's share
 * of that stock. Every field adds: routes to the stock, stocks to the fund,
 * funds to the book.
 *
 *   own        each level-1 holding's whole stored row — what the holding-risk
 *              tables already show, and what a fund's routes sum back to
 *   unmapped   priced leaves no route carries (a fund kept as a NAV leaf, an
 *              ETF, a stock outside the fitted style-factor universe), each
 *              naming the level-1 holding it was reached through in `via`.
 *              They are the "Other" under that holding:
 *
 *                Σ rows under a fund + Σ unmapped via it  =  own[fund]
 *                Σ own  =  simulationRisk.volatility  =  the scenario's stored
 *                          riskMetrics.forecastVolatility
 *
 * `source` says where the figures came from. "stored" is the nightly job's own
 * leaves — fast, and the portfolio's own numbers. "partition" is priced on
 * request from the stored simulation partition — a fund, or a scenario the job
 * has not rerun today — and its first read is slow (about 40 s), so the screen
 * shows a loading state rather than an empty card.
 *
 * `scenario` is the scenario VALUE (`standard_var`, `oil_shock`, …), not its
 * display name; the stress windows are asked with `riskType: "STRESS"`.
 */
export const SIMULATION_RISK_LOOK_THROUGH = gql`
  query SimulationRiskLookThrough(
    $securitySymbol: String!
    $scenario: String! = "standard_var"
    $riskType: String! = "RISK"
  ) {
    lookThroughContributions(securitySymbol: $securitySymbol) {
      securitySymbol
      entityType
      asOfDate
      disclosureDate
      simulationRisk(scenario: $scenario, riskType: $riskType) {
        source
        simulationRunDate
        volatility var95 var99 cvar95 cvar99 expectedReturn
        own { securitySymbol kind weight values { key value } }
        unmapped { securitySymbol kind weight via values { key value } }
      }
      rows {
        pathId
        pathWeight
        resolved
        stockSymbol
        stockName
        path { securitySymbol name kind weight level }
        simulationRisk(scenario: $scenario, riskType: $riskType) {
          volatility var95 var99 cvar95 cvar99 expectedReturn
        }
      }
    }
  }
`;
